import React from 'react';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import Name from 'components/Form/Name';
import Textarea from 'components/Form/Textarea';
import DateControl from 'components/Form/DateControl';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Location from 'components/Form/Location';
import { isPVQForm } from 'helpers/form';
import PVQLocation from 'components/Form/PVQLocation';
import Text from 'components/Form/Text';
import DateRange from 'components/Form/DateRange';
import PVQName from 'components/Form/PVQName';
import { ValidationElementProps } from '../../../Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
} from '../../../../types/props';
import { ErrorCode } from '../../../../redux/modules/application/reducers/error';
import { ForeignBusinessEmploymentItemModel } from '../../../../forms/sections/foreign/business/employment/types';

interface Props extends Partial<ForeignBusinessEmploymentItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  formType?: string
}

export default class JobOffer extends ValidationElement<Props> {
  handleNameError = (value, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,

      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<ForeignBusinessEmploymentItemModel>) => {
    this.props.onUpdate({
      Name: this.props.Name,
      Description: this.props.Description,
      Date: this.props.Date,
      Address: this.props.Address,
      Accepted: this.props.Accepted,
      Explanation: this.props.Explanation,
      AcceptedDate: this.props.AcceptedDate, // PVQ Only
      PerformedService: this.props.PerformedService, // PVQ Only
      DatesPerformed: this.props.DatesPerformed, // PVQ Only
      Compensation: this.props.Compensation, // PVQ Only
      HasOngoingObligations: this.props.HasOngoingObligations, // PVQ Only
      OngoingObligationsExplanation: this.props.OngoingObligationsExplanation, // PVQ Only
      ...queue,
    });
  };

  updateName = (values: ForeignBusinessEmploymentItemModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updateDescription = (values: ForeignBusinessEmploymentItemModel['Description']) => {
    this.update({
      Description: values,
    });
  };

  updateDate = (values: ForeignBusinessEmploymentItemModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateAddress = (values: ForeignBusinessEmploymentItemModel['Address']) => {
    this.update({
      Address: values,
    });
  };

  updateAccepted = (values: ForeignBusinessEmploymentItemModel['Accepted']) => {
    this.update({
      Accepted: values,
      AcceptedDate: values.value === 'Yes' ? this.props.AcceptedDate : {},
      PerformedService: values.value === 'Yes' ? this.props.PerformedService : {},
      DatesPerformed: values.value === 'Yes' ? this.props.DatesPerformed : {},
      Compensation: values.value === 'Yes' ? this.props.Compensation : {},
      HasOngoingObligations: values.value === 'Yes' ? this.props.HasOngoingObligations : {},
      OngoingObligationsExplanation: values.value === 'Yes' ? this.props.OngoingObligationsExplanation : {},
      Explanation: values.value === 'No' ? this.props.Explanation : {},
    });
  };

  updateExplanation = (values: ForeignBusinessEmploymentItemModel['Explanation']) => {
    this.update({
      Explanation: values,
    });
  };

  updateAcceptedDate = (values: ForeignBusinessEmploymentItemModel['AcceptedDate']) => {
    this.update({
      AcceptedDate: values,
    });
  };

  updatePerformedService = (values: ForeignBusinessEmploymentItemModel['PerformedService']) => {
    this.update({
      PerformedService: values,
      DatesPerformed: values.value === 'Yes' ? this.props.DatesPerformed : {},
      Compensation: values.value === 'Yes' ? this.props.Compensation : {},
      HasOngoingObligations: values.value === 'Yes' ? this.props.HasOngoingObligations : {},
      OngoingObligationsExplanation: values.value === 'Yes' ? this.props.OngoingObligationsExplanation : {},
    });
  };

  updateDatesPerformed = (values: ForeignBusinessEmploymentItemModel['DatesPerformed']) => {
    this.update({ DatesPerformed: values });
  };

  updateCompensation = (values: ForeignBusinessEmploymentItemModel['Compensation']) => {
    this.update({
      Compensation: values,
    });
  };

  updateHasOngoingObligations = (values: ForeignBusinessEmploymentItemModel['HasOngoingObligations']) => {
    this.update({
      HasOngoingObligations: values,
    });
  };

  updateOngoingObligationsExplanation = (values: ForeignBusinessEmploymentItemModel['OngoingObligationsExplanation']) => {
    this.update({
      OngoingObligationsExplanation: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);
    return isPVQ ? (
      <div className="job-offer">
        <Field
          title={i18n.t('foreign.pvq.business.employment.heading.name')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <PVQName
            {...this.props.Name}
            name="Name"
            className="employment-name"
            aria-label="Provide the name of the foreign national who made the offer"
            onUpdate={this.updateName}
            onError={this.handleNameError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Name`}
            showLastFirstMiddleNameOrder
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.employment.heading.description')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Description`}
        >
          <Textarea
            {...this.props.Description}
            name="Description"
            className="employment-description"
            onUpdate={this.updateDescription}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Description`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.employment.heading.date')}
          help="foreign.pvq.business.employment.help.date"
          adjustFor="label"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
          exactAttribute
        >
          <DateControl
            {...this.props.Date}
            name="Date"
            className="employment-date"
            minDateEqualTo
            onUpdate={this.updateDate}
            onError={this.props.onError}
            required={this.props.required}
            hideDay
            attribute={`${attribute}.Date`}
          />
        </Field>

        <Field
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Address`}
          adjustFor="address"
        >
          <PVQLocation
            {...this.props.Address}
            attribute={`${attribute}.Address`}
            name="address"
            addressBooks={this.props.addressBooks}
            addressBook="ForeignJobOffer"
            onUpdate={this.updateAddress} // eslint-disable-line @typescript-eslint/unbound-method
            onError={this.props.onError}
            dispatch={this.props.dispatch}
            required={this.props.required}
            geocode
            showInternationalStreet={false}
            showMilitaryInstallation={false}
            label={i18n.t('foreign.pvq.business.employment.heading.address')}
            domesticTitle={i18n.t(`foreign.pvq.business.employment.heading.addressDomesticTitle`)}
            foreignTitle={i18n.t(`foreign.pvq.business.employment.heading.addressForeignTitle`)}
          />
        </Field>

        <Branch
          {...this.props.Accepted}
          name="Accepted"
          label={i18n.t('foreign.pvq.business.employment.heading.accepted')}
          labelSize="h4"
          className={`employment-accepted ${(this.props.Accepted || {}).value ? 'no-margin-bottom' : ''
          }`.trim()}
          onUpdate={this.updateAccepted}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Accepted`}
          warning
        />

        {(this.props.Accepted?.value === 'Yes') && (
          <>
            <Field
              title={i18n.t('foreign.pvq.business.employment.heading.acceptedDate')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.AcceptedDate`}
            >
              <DateControl
                {...this.props.AcceptedDate}
                name="Date"
                className="employment-accepted-date"
                minDateEqualTo
                onUpdate={this.updateAcceptedDate}
                onError={this.props.onError}
                required={this.props.required}
                hideDay
                attribute={`${attribute}.AcceptedDate`}
              />
            </Field>

            <Branch
              {...this.props.PerformedService}
              name="Accepted"
              label={i18n.t('foreign.pvq.business.employment.heading.performedService')}
              labelSize="h4"
              className="performed-service"
              onUpdate={this.updatePerformedService}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.PerformedService`}
              warning
            />

            {(this.props.PerformedService?.value === 'Yes') && (
            <>
              <Field
                title={i18n.t('foreign.pvq.business.employment.heading.datesPerformed')}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.DatesPerformed`}
              >
                <DateRange
                  {...this.props.DatesPerformed}
                  attribute={`${attribute}.DatesPerformed`}
                  name="DatesPerformed"
                  className="dates-performed"
                  minDateEqualTo
                  onUpdate={this.updateDatesPerformed}
                  onError={this.props.onError}
                  required={this.props.required}
                  hideFromDay
                  hideToDay
                />
              </Field>

              <Field
                title={i18n.t('foreign.pvq.business.employment.heading.compensation')}
                attribute={`${attribute}.Compensation`}
                scrollIntoView={this.props.scrollIntoView}
              >
                <Text
                  {...this.props.Compensation}
                  name="Compensation"
                  attribute={`${attribute}.Compensation`}
                  onUpdate={this.updateCompensation}
                  onError={this.props.onError}
                  className="compensation"
                  required={this.props.required}
                />
              </Field>

              <Branch
                {...this.props.HasOngoingObligations}
                name="Accepted"
                label={i18n.t('foreign.pvq.business.employment.heading.hasOngoingObligations')}
                labelSize="h4"
                className="has-ongoing-obligations"
                onUpdate={this.updateHasOngoingObligations}
                onError={this.props.onError}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.HasOngoingObligations`}
              />
              <Field
                title={i18n.t('foreign.pvq.business.employment.label.explanation')}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.OngoingObligationsExplanation`}
              >
                <Textarea
                  {...this.props.OngoingObligationsExplanation}
                  name="OngoingObligationsExplanation"
                  className="ongoing-obligations-explanation"
                  onUpdate={this.updateOngoingObligationsExplanation}
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.OngoingObligationsExplanation`}
                />
              </Field>
            </>
            )}
          </>
        )}

        {(this.props.Accepted?.value === 'No') && (
        <Field
          title={i18n.t('foreign.pvq.business.employment.label.explanation')}
          titleSize="label"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Explanation`}
        >
          <Textarea
            {...this.props.Explanation}
            name="Explanation"
            className="explanation"
            onUpdate={this.updateExplanation}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Explanation`}
          />
        </Field>
        )}
      </div>
    ) : (
      <div className="job-offer">
        <Field
          title={i18n.t('foreign.business.employment.heading.name')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Name
            name="Name"
            className="employment-name"
            aria-label="Provide the name of the foreign national who made the offer"
            onUpdate={this.updateName}
            onError={this.handleNameError}
            {...this.props.Name}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Name`}
          />
        </Field>
        <Field
          title={i18n.t('foreign.business.employment.heading.description')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Description`}
        >
          <Textarea
            name="Description"
            className="employment-description"
            onUpdate={this.updateDescription}
            onError={this.props.onError}
            {...this.props.Description}
            required={this.props.required}
            attribute={`${attribute}.Description`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.employment.heading.date')}
          help="foreign.business.employment.help.date"
          adjustFor="label"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
        >
          <DateControl
            name="Date"
            {...this.props.Date}
            className="employment-date"
            minDateEqualTo
            onUpdate={this.updateDate}
            onError={this.props.onError}
            required={this.props.required}
            hideDay
            attribute={`${attribute}.Date`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.employment.heading.address')}
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Address`}
        >
          <Location
            name="Address"
            {...this.props.Address}
            label={i18n.t('foreign.business.employment.label.address')}
            layout={Location.US_CITY_STATE_ZIP_INTERNATIONAL_CITY}
            className="employment-address"
            showPostOffice
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Address`}
          />
        </Field>

        <Branch
          name="Accepted"
          label={i18n.t('foreign.business.employment.heading.accepted')}
          labelSize="h4"
          className={`employment-accepted ${
            (this.props.Accepted || {}).value ? 'no-margin-bottom' : ''
          }`.trim()}
          {...this.props.Accepted}
          onUpdate={this.updateAccepted}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Accepted`}
        />

        <Show when={(this.props.Accepted || {}).value}>
          <Field
            title={i18n.t('foreign.business.employment.label.explanation')}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Explanation`}
          >
            <Textarea
              name="Explanation"
              {...this.props.Explanation}
              className="employment-explanation"
              onUpdate={this.updateExplanation}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Explanation`}
            />
          </Field>
        </Show>
      </div>
    );
  }
}

JobOffer.defaultProps = {
  name: 'Item',
  Name: {},
  Description: {},
  Date: {},
  Address: {},
  Accepted: {},
  Explanation: {},
  AcceptedDate: {}, // PVQ Only
  PerformedService: {}, // PVQ Only
  DatesPerformed: {}, // PVQ Only
  Compensation: {}, // PVQ Only
  HasOngoingObligations: {}, // PVQ Only
  OngoingObligationsExplanation: {}, // PVQ Only
  onUpdate: (queue) => {},
  onError: (value, arr: ErrorCode) => arr,
};
